import { isFunction, isObject, mapValues } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { NEW_VIEW } from '@/constants/dashboardConstants';
import useColumns from '@API/project/useColumns';
import useProject from '@API/project/useProject';
import useSelectedProjectView from '@API/project/useSelectedProjectView';
import {
  DashboardView,
  DashboardViewUpdatePayload,
  ProjectColumn
} from '@experiment-management-shared/types';
import { fromTemplateToDashboard } from '@experiment-management-shared/utils/EMView';
import { shouldBeLocalTemplateSaved } from '@projects/components/EMDashboardHeader/DashboardViewSelect/utils';
import useDeepMemo from '@shared/hooks/useDeepMemo';
import isEqual from 'fast-deep-equal';
import { useDashboardApiContext } from '../useDashboardAPIContext';
import { useAutoGeneratedView } from './useAutoGeneratedView';
import useDashboardAutosaveView from './useDashboardAutosaveView';
import useShowPublicViewChangedNotification from './useShowPublicViewChangedNotification';

export type DashboardViewUpdateGetter = (
  d: DashboardView
) => DashboardViewUpdatePayload;

type DashboardChanges = DashboardViewUpdatePayload | DashboardViewUpdateGetter;

const useDashboardSelectedView = () => {
  const [dashboardView, setDashboardView] = useState<DashboardView | null>(
    null
  );
  const [experimentKeys, setExperimentKeys] = useState<string[] | null>(null);
  const { viewId } = useParams<{ viewId?: string }>();
  const { data: project } = useProject();
  const { data: columns } = useColumns() as {
    data: ProjectColumn[] | undefined;
  };
  const {
    data: selectedProjectView,
    isSuccess: hasSelectedProjectViewLoaded
  } = useSelectedProjectView();
  const defaultView = useAutoGeneratedView({
    enabled: viewId === NEW_VIEW,
    experimentKeys
  });
  const { saveTemplateOnServerDebounced } = useDashboardApiContext();
  const autosaveDashboardView = useDashboardAutosaveView();
  const {
    showPublicViewChangedNotification
  } = useShowPublicViewChangedNotification();

  const canEdit = project?.canEdit;

  const selectedDashboardView = useDeepMemo(() => {
    if (!columns || !hasSelectedProjectViewLoaded) return null;

    return selectedProjectView
      ? fromTemplateToDashboard(selectedProjectView, columns)
      : defaultView;
  }, [columns, defaultView, hasSelectedProjectViewLoaded, selectedProjectView]);

  // on change the visible experiment keys, it'll trigger to refetch the auto-generated panels
  // when it's done, we should update the sections with the new ones
  // (if view is personal view and not frozen)
  const autoGeneratedSections = defaultView?.panels.sections;
  useEffect(() => {
    if (
      autoGeneratedSections &&
      viewId === NEW_VIEW &&
      !dashboardView?.isFrozenView
    ) {
      setDashboardView(previousDashboardView => {
        if (!previousDashboardView) return previousDashboardView;

        if (
          isEqual(previousDashboardView.panels.sections, autoGeneratedSections)
        ) {
          return previousDashboardView;
        }

        return {
          ...previousDashboardView,
          panels: {
            ...previousDashboardView?.panels,
            sections: autoGeneratedSections
          }
        } as DashboardView;
      });
    }
  }, [autoGeneratedSections]);

  // set the initial dashboard view from current view
  useEffect(() => {
    setDashboardView(previousDashboardView => {
      return previousDashboardView ?? selectedDashboardView;
    });
  }, [dashboardView, selectedDashboardView]);

  // replace dashboard view on change the view ID
  // @todo: replace this `useEffect` to trigger it on events instead
  useEffect(() => {
    setDashboardView(selectedDashboardView);
  }, [viewId]);

  // save view on unmount + on change view
  useEffect(() => {
    return () => {
      saveTemplateOnServerDebounced.flush();
    };
  }, [saveTemplateOnServerDebounced, viewId]);

  const updateDashboardView = useCallback(
    (changesOrGetter: DashboardChanges) => {
      setDashboardView(previousDashboardView => {
        if (!previousDashboardView) return previousDashboardView;

        const changes = isFunction(changesOrGetter)
          ? changesOrGetter(previousDashboardView)
          : changesOrGetter;

        const newDashboardView = applyDashboardViewChanges(
          previousDashboardView,
          changes
        );

        if (
          newDashboardView !== null &&
          newDashboardView !== previousDashboardView
        ) {
          if (shouldFreezeView(previousDashboardView, newDashboardView)) {
            newDashboardView.isFrozenView = true;
          }

          if (shouldBeLocalTemplateSaved(newDashboardView)) {
            autosaveDashboardView(newDashboardView);
            return newDashboardView;
          }

          if (!canEdit) {
            showPublicViewChangedNotification(project);
            return newDashboardView;
          }

          autosaveDashboardView(newDashboardView);
        }

        return newDashboardView;
      });
    },
    [canEdit, showPublicViewChangedNotification, project]
  );

  const recalculateAutoGeneratedPanels = useCallback(newExperimentKeys => {
    setExperimentKeys(newExperimentKeys);
  }, []);

  // forces to trigger the previous `useEffect` to set the dashboard view
  // with the value of `selectedDashboardView`
  // making a `setDashboardView(selectedDashboardView)` won't work
  // because where it's called it could have an old value of it
  // (old callback reference = old `selectedDashboardView` reference)
  const resetDashboardView = useCallback(() => {
    setDashboardView(null);
  }, []);

  return {
    dashboardView,
    recalculateAutoGeneratedPanels,
    resetDashboardView,
    updateDashboardView
  };
};

function applyDashboardViewChanges(
  previousDashboardView: DashboardView | null,
  changes: DashboardViewUpdatePayload
) {
  const updatedView = mapValues(
    previousDashboardView,
    (value, key: keyof DashboardView) => {
      const payloadValue = changes[key];

      if (!payloadValue) return value;
      if (Array.isArray(payloadValue)) return payloadValue;
      if (isObject(value) && isObject(payloadValue)) {
        return {
          ...value,
          ...payloadValue
        };
      }

      return payloadValue;
    }
  );

  if (isEqual(previousDashboardView, updatedView)) {
    return previousDashboardView;
  }

  return (updatedView as unknown) as DashboardView;
}

function shouldFreezeView(
  previousDashboardView: DashboardView | null,
  newDashboardView: DashboardView | null
) {
  if (!previousDashboardView || !newDashboardView) return false;
  if (!previousDashboardView?.isPersonalView) return false;
  if (previousDashboardView?.isFrozenView) return true;

  const previousAutoGeneratedPanels = getPanelsFromDashboardView(
    previousDashboardView
  );
  const newAutoGeneratedPanels = getPanelsFromDashboardView(newDashboardView);

  if (previousAutoGeneratedPanels.length !== newAutoGeneratedPanels.length) {
    return true;
  }

  return !isEqual(
    previousDashboardView.panels.sections.map(section => section.layout),
    newDashboardView.panels.sections.map(section => section.layout)
  );
}

function getPanelsFromDashboardView(view: DashboardView) {
  return view.panels.sections
    .map(section => {
      return section.panels.filter(panel => panel.autoGenerated);
    })
    .flat();
}

export default useDashboardSelectedView;
